<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted } from 'vue'
import EntranceStep from './_partials/Entrance.vue'
import ForWorkMailAddressStep from './_partials/ForWorkMailAddress.vue'
import ForPersonalMailAddressStep from './_partials/ForPersonalMailAddress.vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import SplitTestWrapper from '@/components/marketing/SplitTestWrapper.vue'
import stateInitializer from '@/models/stateInitializer'
import leadGen from '@/models/leadGen'
import { sendGtmEvent } from '@/utilities/googleTagManager'
import { UrlUtil } from '@/utilities/url'
import {
  newUserPasswordPath,
  newUserRegistrationPath,
  userFacebookOmniauthAuthorizePath,
  googleOauth2ApiAuthPath,
} from '@/generated/railsRoutes'

import type { Ref } from 'vue'

export default defineComponent({
  name: 'UsersSignUpMain',

  components: {
    EntranceStep,
    ForWorkMailAddressStep,
    ForPersonalMailAddressStep,
    ButtonSubmit,
    SplitTestWrapper,
  },

  emits: ['step:last', 'step:entrance'],

  setup(_, { emit }) {
    // constants
    const GTM_EVENT_NAME = 'registration_page_focus2' as const
    const PHASE = ['ENTRANCE', 'INPUT_PROFILE', 'CONFIRMATION'] as const

    // local type
    type PhaseType = typeof PHASE[number]

    // data
    interface State {
      form: {
        signUp: Form,
      },

      input: {
        signUp: {
          account: {
            mailAddress: InputText,
          },
          profile: {
            firstName: InputText,
            lastName: InputText,
            firstNameKana: InputText,
            lastNameKana: InputText,
            workMailAddress: InputText,
            phoneNumber: InputText,
            companyName: InputText,
            corporateZipcode: InputText,
            corporateAddress1: InputText,
            corporateAddress2: InputText,
            corporateNumber: InputText,
            corporateEmployeeNum: InputNumber,
            industry: InputText,
            scale: InputText,
            listingStatus: InputText,
            profession: InputText,
            professionDomain: InputMultiSelect,
            positionLevel: InputText,
            departmentAndPosition: InputText,
          },
          optionalNewsletterSettings: OptionalNewsletterSettings,
          oAuth: {
            provider: InputText,
          },
        },
      },

      oAuth: {
        /**
         * ソーシャルボタンの Callback かどうか
         *
         * @description
         *  登録導線において、ソーシャルボタンをクリックして OAuth 認証を通した後、
         *  Callback として登録画面の最初に帰って来るが、その際サーバから use_omniauth = true で返却される。
         *  その場合（OAuth 認証後の Callbackの場合）は、ソーシャルボタンを表示しない・メルアド欄が readonly になる等の制御が入る。
         */
        isCallback: boolean,

        /**
         * サーバ側で管理しているソーシャルサービスのサービス名ラベル
         */
        label: string,
      },
    }
    const state: State = reactive({
      form: {
        signUp: {
          action: '',
          method: 'post',
          name: '',
        },
      },
      input: {
        signUp: {
          account: {
            mailAddress: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
                email: true,
              },
            },
          },
          profile: {
            firstName: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            lastName: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            firstNameKana: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
                kana: true,
              },
            },
            lastNameKana: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
                kana: true,
              },
            },
            workMailAddress: {
              value: '',
              name: '',
              validationScheme: {},
            },
            phoneNumber: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
                phoneNumber: true,
              },
            },
            companyName: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            corporateZipcode: {
              value: '',
              name: '',
              validationScheme: {},
            },
            corporateAddress1: {
              value: '',
              name: '',
              validationScheme: {},
            },
            corporateAddress2: {
              value: '',
              name: '',
              validationScheme: {},
            },
            corporateNumber: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            corporateEmployeeNum: {
              value: undefined,
              name: '',
              validationScheme: {},
            },
            industry: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            scale: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            listingStatus: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            profession: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            professionDomain: {
              value: [],
              name: '',
              validationScheme: {},
            },
            positionLevel: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
            departmentAndPosition: {
              value: '',
              name: '',
              validationScheme: {
                required: true,
              },
            },
          },

          optionalNewsletterSettings: {
            mailStopFlag: {
              value: false,
              name: '',
              validationScheme: {},
            },
            mailNewsStopFlag: {
              value: false,
              name: '',
              validationScheme: {},
            },
            mailExpoStopFlag: {
              value: false,
              name: '',
              validationScheme: {},
            },
            mailAiStopFlag: {
              value: false,
              name: '',
              validationScheme: {},
            },
          },

          oAuth: {
            provider: {
              value: '',
              name: '',
              validationScheme: {},
            },
          },
        }
      },

      oAuth: {
        isCallback: false,
        label: '',
      }
    })
    stateInitializer(state, 'main')

    const {
      selectCompanyLeadInfo,
      inputCompanyName,
    } = leadGen(state.input.signUp.profile)

    // constants
    /**
     * ステップカウンターの offset 値（値のずらし幅）
     * 最初のメールアドレス入力画面はステップウィザードの管理外であり、ステップの表示も出ないが、その分のステップも後続ステップは加味するため、その分を offset 値として加算管理する
     */
    const STEP_OFFSET = 1 as const

    const isFullFilled = ref(false)
    const phase: Ref<PhaseType> = ref(PHASE[0])
    const isFreeMailDomain = ref(false)

    const currentStep = ref(0)
    const maxStep = ref(0)

    const url = new UrlUtil()
    const trcdParam = url.getQueryParameter('trcd') || ''

    const pageHeadingPattern = computed(() => {
      // trcdParam は brw_campaign_about_bizhint_media / app_campaign_about_bizhint_media のように app / brw どちらの prefix も取りうる。
      return /^(app|brw)_campaign_about_bizhint_media/.test(trcdParam)
        ? 'media'
        : /^(app|brw)_campaign_about_bizhint_mail_campaign/.test(trcdParam)
          ? 'mail_campaign'
          : 'default'
    })

    onMounted(() => {
      emit('step:entrance', phase.value === PHASE[0])
    })

    const transitionFromEntrance = ($isFreeMailDomain: boolean) => {
      isFreeMailDomain.value = $isFreeMailDomain

      /**
       * 登録メールアドレスに入力されたメールドメインがフリーメールでなかった場合は、企業メールドメインとみなし、
       * 勤務先メールアドレスにも同じメールアドレスで自動補完し（アカウント登録後のユーザの手間の軽減のため）、
       * form submit の対象になる。（hidden で送られる）
       */
      state.input.signUp.profile.workMailAddress.value = !isFreeMailDomain.value
        ? state.input.signUp.account.mailAddress.value
        : ''

      phaseNext()
    }

    const phaseNext = () => {
      if (phase.value === PHASE[PHASE.length - 1]) return
      phase.value = PHASE[PHASE.indexOf(phase.value) + 1]

      // ヘッダーのロゴを非リンク化する（エントランスにいるとき以外は極力離脱を防ぎたいため）
      emit('step:entrance', false)
    }

    const phaseBack = () => {
      if (phase.value === PHASE[0]) return
      phase.value = PHASE[PHASE.indexOf(phase.value) - 1]

      // エントランスに戻ったらヘッダーのロゴをリンク化する
      if (phase.value === PHASE[0]) emit('step:entrance', true)
    }

    const completed = () => {
      sendGtmEvent(GTM_EVENT_NAME, '08_submit')
      isFullFilled.value = true
      emit('step:last')
    }

    return {
      // constants
      GTM_EVENT_NAME,
      PHASE,
      STEP_OFFSET,

      // routes
      newUserPasswordPath,
      newUserRegistrationPath,
      userFacebookOmniauthAuthorizePath,
      googleOauth2ApiAuthPath,

      // data
      state,
      phase,
      isFreeMailDomain,
      currentStep,
      maxStep,

      // computed
      isFullFilled,
      pageHeadingPattern,

      // methods
      selectCompanyLeadInfo,
      inputCompanyName,
      transitionFromEntrance,
      phaseNext,
      phaseBack,
      completed,
      sendGtmEvent,
    }
  },
})
</script>

<template>
  <FormDefault
    :name="state.form.signUp.name"
    :action="state.form.signUp.action"
    :method="state.form.signUp.method"
    :before-action="() => sendGtmEvent(GTM_EVENT_NAME, '09_submit')">
    <section class="f-section__signUup">
      <transition name="is-fade__long">
        <div
          v-show="!isFullFilled"
          class="f-section__signUup--body">
          <div
            class="l-cols__signUp"
            :data-step="currentStep">
            <div class="l-cols__signUp--col is-primary">
              <div class="l-rows__signUpInput">
                <div
                  :class="[
                    'l-rows__signUpInput--row',
                    { 'is-active': phase === PHASE[0] },
                  ]">
                  <EntranceStep
                    namespace="userRegistrationEntrance"
                    :first-name="state.input.signUp.profile.firstName"
                    :last-name="state.input.signUp.profile.lastName"
                    :first-name-kana="state.input.signUp.profile.firstNameKana"
                    :last-name-kana="state.input.signUp.profile.lastNameKana"
                    :mail-address="state.input.signUp.account.mailAddress"
                    :is-o-auth-callback="state.oAuth.isCallback"
                    @update:first-name="state.input.signUp.profile.firstName.value = $event"
                    @update:last-name="state.input.signUp.profile.lastName.value = $event"
                    @update:first-name-kana="state.input.signUp.profile.firstNameKana.value = $event"
                    @update:last-name-kana="state.input.signUp.profile.lastNameKana.value = $event"
                    @update:mail-address="state.input.signUp.account.mailAddress.value = $event"
                    @click:next="transitionFromEntrance($event)">
                    <template #default>
                      <!--
                        企業メールドメインが入力された場合は、
                        アカウント登録メールアドレスとは別に、
                        勤務先メールアドレスにも同情報で自動補完しておく。
                        エントランスステップの情報なので検査区画も同ステップに置く（validationScheme が空の間は検査対象にならない）
                      -->
                      <ValidationProvider
                        v-if="state.input.signUp.profile.workMailAddress.value"
                        :value="state.input.signUp.profile.workMailAddress.value"
                        namespace="userRegistrationEntrance"
                        :field="state.input.signUp.profile.workMailAddress.name"
                        :scheme="state.input.signUp.profile.workMailAddress.validationScheme"
                        :show-message="false">
                        <input
                          class="input"
                          type="hidden"
                          :name="state.input.signUp.profile.workMailAddress.name"
                          :value="state.input.signUp.profile.workMailAddress.value">
                      </ValidationProvider>
                    </template>
                  </EntranceStep>
                <!-- /.l-rows__signUpInput row --></div>

                <div
                  :class="[
                    'l-rows__signUpInput--row',
                    { 'is-active': phase === PHASE[1] },
                  ]">
                  <div class="l-unit__profileInput">
                    <div class="l-unit__profileInput--description">
                      <!-- 個人メールアドレス入力者向けルート -->
                      <ForPersonalMailAddressStep
                        v-if="isFreeMailDomain"
                        :scale="state.input.signUp.profile.scale"
                        :industry="state.input.signUp.profile.industry"
                        :position-level="state.input.signUp.profile.positionLevel"
                        :profession="state.input.signUp.profile.profession"
                        :profession-domain="state.input.signUp.profile.professionDomain"
                        :listing-status="state.input.signUp.profile.listingStatus"
                        :optional-newsletter-settings="state.input.signUp.optionalNewsletterSettings"
                        @update:scale="state.input.signUp.profile.scale.value = $event"
                        @update:industry="state.input.signUp.profile.industry.value = $event"
                        @update:position-level="state.input.signUp.profile.positionLevel.value = $event"
                        @update:profession="state.input.signUp.profile.profession.value = $event"
                        @update:profession-domain="state.input.signUp.profile.professionDomain.value = $event"
                        @update:listing-status="state.input.signUp.profile.listingStatus.value = $event"
                        @update:optional-newsletter-settings="state.input.signUp.optionalNewsletterSettings = $event"
                        @return:entrance="phaseBack"
                        @transition:step="currentStep = $event"
                        @resolved:maxStep="maxStep = $event"
                        @completed="completed" />

                      <!-- 企業メールドメイン入力者向けルート -->
                      <ForWorkMailAddressStep
                        v-else
                        :company-name="state.input.signUp.profile.companyName"
                        :corporate-zipcode="state.input.signUp.profile.corporateZipcode"
                        :corporate-address1="state.input.signUp.profile.corporateAddress1"
                        :corporate-address2="state.input.signUp.profile.corporateAddress2"
                        :corporate-number="state.input.signUp.profile.corporateNumber"
                        :corporate-employee-num="state.input.signUp.profile.corporateEmployeeNum"
                        :department-and-position="state.input.signUp.profile.departmentAndPosition"
                        :phone-number="state.input.signUp.profile.phoneNumber"
                        :scale="state.input.signUp.profile.scale"
                        :industry="state.input.signUp.profile.industry"
                        :position-level="state.input.signUp.profile.positionLevel"
                        :profession="state.input.signUp.profile.profession"
                        :profession-domain="state.input.signUp.profile.professionDomain"
                        :listing-status="state.input.signUp.profile.listingStatus"
                        :optional-newsletter-settings="state.input.signUp.optionalNewsletterSettings"
                        @update:company-name="inputCompanyName($event)"
                        @select:company-lead-info="selectCompanyLeadInfo($event)"
                        @update:department-and-position="state.input.signUp.profile.departmentAndPosition.value = $event"
                        @update:phone-number="state.input.signUp.profile.phoneNumber.value = $event"
                        @update:scale="state.input.signUp.profile.scale.value = $event"
                        @update:industry="state.input.signUp.profile.industry.value = $event"
                        @update:position-level="state.input.signUp.profile.positionLevel.value = $event"
                        @update:profession="state.input.signUp.profile.profession.value = $event"
                        @update:profession-domain="state.input.signUp.profile.professionDomain.value = $event"
                        @update:listing-status="state.input.signUp.profile.listingStatus.value = $event"
                        @update:optional-newsletter-settings="state.input.signUp.optionalNewsletterSettings = $event"
                        @return:entrance="phaseBack"
                        @transition:step="currentStep = $event"
                        @resolved:maxStep="maxStep = $event"
                        @completed="completed" />
                    <!-- /.l-unit__profileInput description --></div>

                    <div class="l-unit__profileInput--footer is-stepCounter">
                      <div
                        class="o-label__stepCounter"
                        :style="{
                          '--max-step': maxStep + STEP_OFFSET,
                          '--assigned-step': currentStep + STEP_OFFSET,
                        }">
                        <p class="o-label__stepCounter--item">
                          {{ currentStep + STEP_OFFSET }}/{{ maxStep + STEP_OFFSET }}
                        </p>
                      <!-- /.o-label__stepCounter --></div>
                    <!-- /.l-unit__profileInput footer --></div>
                  <!-- /.l-unit__profileInput --></div>
                <!-- /.l-rows__signUpInput row --></div>
              <!-- /.l-rows__signUpInput --></div>
            <!-- /.l-cols__signUp col --></div>

            <div class="l-cols__signUp--col is-secondary">
              <section class="f-section__serviceIntroduction">
                <div class="f-section__serviceIntroduction--body">
                  <h2 class="h2 u-text__xx-huge u-text__bold">
                    <SplitTestWrapper
                      v-if="pageHeadingPattern"
                      :pattern="pageHeadingPattern">
                      <template #media>
                        ここでしか読めない
                        <span class="span u-text__block">
                          経営者のための
                        </span>
                        <span class="span u-text__block">
                          事例メディア
                        </span>
                      </template>
                      <template #mail_campaign>
                        ここでしか読めない
                        <span class="span u-text__block">
                          経営者のための
                        </span>
                        <span class="span u-text__block">
                          事例ニュースレター
                        </span>が届く
                      </template>
                      <template #default>
                        本気でチャレンジする企業に「誇り」を。
                      </template>
                    </SplitTestWrapper>
                  </h2>

                  <div class="o-list__bizhintFeature">
                    <dl class="o-list__bizhintFeature--unit">
                      <dt class="o-list__bizhintFeature--term">
                        BizHintの特徴
                      </dt>
                      <dd class="o-list__bizhintFeature--description">
                        経営に役立つニュースレターが届く
                      </dd>
                      <dd class="o-list__bizhintFeature--description">
                        経営危機を克服して躍進する中小企業などの本音インタビューが読める
                      </dd>
                      <dd class="o-list__bizhintFeature--description">
                        DX推進など注目イベントに参加できる
                      </dd>
                    </dl>
                  <!-- /.o-list__bizhintFeature --></div>

                  <p class="p u-text__small">
                    全国の経営者が抱える悩みに正面から向き合い、課題解決に繋がる情報を発信してまいります。
                  </p>
                <!-- /.f-section__serviceIntroduction body --></div>
              <!-- /.f-section__serviceIntroduction --></section>
            <!-- /.l-cols__signUp col --></div>
          <!-- /.l-cols__signUp --></div>
        <!-- /.f-section__signUup body --></div>
      </transition>

      <transition name="is-fade__long">
        <div
          v-show="isFullFilled"
          class="f-section__signUup--body is-confirm">
          <div class="o-heading__section">
            <h2 class="o-heading__section--item">
              確認メールの送信
            </h2>
          <!-- /.o-heading__section --></div>

          <p
            v-if="state.oAuth.isCallback"
            class="p u-text__center">
            ご入力ありがとうございました。<br>
            下記ボタンを押して確認を完了してください。
          </p>
          <p
            v-else
            class="p u-text__center">
            ご入力ありがとうございました。<br>
            以下のメールアドレスに登録確認のメールを送信します。よろしいですか？
          </p>

          <div class="o-label__mailAddress">
            <p class="o-label__mailAddress--item">
              {{ state.input.signUp.account.mailAddress.value }}
            </p>
          <!-- /.o-label__mailAddress --></div>

          <input
            v-if="state.oAuth.isCallback"
            type="hidden"
            :name="state.input.signUp.oAuth.provider.name"
            :value="state.input.signUp.oAuth.provider.value">

          <ButtonSubmit
            :is-link="false"
            :is-submit="true"
            :width="384">
            {{
              state.oAuth.isCallback
                ? `${state.oAuth.label}アカウントで登録確認する`
                : '確認メールを送信する'
            }}
          </ButtonSubmit>
        <!-- /.f-section__signUup body --></div>
      </transition>
    <!-- /.f-section__signUup --></section>
  </FormDefault>
</template>

<style lang="sass" scoped>
// foundation
.f-section__signUup
  $root: &
  width: 100%
  display: grid
  grid-template-columns: 1fr
  grid-template-rows: auto auto
  margin-top: changeSizePx2Em(12)

  & &--body
    grid-column: 1 / 2
    grid-row: 1 / 3

    &.is-confirm
      width: 100%
      max-width: 700px
      grid-row: 1 / 2
      justify-self: center
      padding: 1.5em 3em
      background-color: rgba($bg-white, 1)
      box-shadow: 0 3px 30px rgba($bg-black, 0.16)

  +breakpoint
    display: block
    margin-top: 0

    #{$root}--body
      &.is-confirm
        padding:
          left: 1.5em
          right: 1.5em

// layout
.l-unit__profileInput
  position: relative

  & &--footer
    position: absolute
    top: 1.5em  // same size on l-unit__signUpStep padding
    right: 3em

.l-cols__signUp
  $root: &
  $primary-width-px: 600
  $secondary-width-px: $global-contents-width-pc - $primary-width-px
  display: flex
  position: relative

  & &--col
    position: relative

    &.is-primary
      width: getRelativeValue($primary-width-px, $global-contents-width-pc)
      order: 1
      background-color: rgba($bg-white, 1)
      box-shadow: 0 3px 30px rgba($bg-black, 0.16)

    &.is-secondary
      width: getRelativeValue($secondary-width-px, $global-contents-width-pc)
      padding: 80px 32px
      background:
        color: rgba($bg-white, 1)
        image: url(@/images/account/bg_signUp_01.svg)
        position: center bottom -80px
        repeat: no-repeat

  +breakpoint
    $image-width-px: 1280
    $image-height-px: 848
    display: block

    #{$root}--col
      &.is-primary
        width: auto

      &.is-secondary
        display: none

.l-rows__signUpInput
  $root: &
  display: grid

  & &--row
    grid-row: 1 / 2
    grid-column: 1 / 2
    visibility: visible
    +transition

    &:not(.is-active)
      transform: scale(0.9)
      visibility: hidden
      opacity: 0
      pointer-events: none

// object
.o-heading__section
  margin-bottom: 1.5em

  & &--item
    line-height: 1.4
    font:
      size: $font-large
      weight: bold
    text-align: center

.o-list__bizhintFeature
  $root: &
  margin-top: 1em

  &:first-child
    margin-top: 0

  & &--term
    color: $font-dark-orange
    font-weight: bold

  $bullet-size: 0.5em
  $font-size: 1em
  $line-height: 1.6
  & &--description
    display: grid
    grid-template-columns: $bullet-size 1fr
    gap: 0.5em
    position: relative
    line-height: $line-height
    font-size: $font-size
    margin-top: 0.5em

    &:first-child
      margin-top: 0

    &::before
      content: ''
      width: $bullet-size
      height: $bullet-size
      display: block
      margin:
        top: math.div($font-size * $line-height - $bullet-size, 2)
      background-color: rgba($bg-dark-orange, 1)
      border-radius: 50%

.o-label__mailAddress
  $root: &
  display: flex
  justify-content: center
  margin-top: 1em

  &:first-child
    margin-top: 0

  & &--item
    min-width: changeSizePx2Em(384)
    max-width: 100%
    overflow: auto
    line-height: 1.4
    font-weight: bold
    text-align: center
    white-space: nowrap
    padding: changeSizePx2Em(12)
    background-color: rgba($bg-xx-light-gray, 1)
    border: 1px solid $border-x-light-gray

  +breakpoint($max-media: sp)
    #{$root}--item
      width: 100%
      min-width: 0

.o-label__stepCounter
  $root: &
  --max-step: 6
  --assigned-step: 1

  & &--item
    $step-meter: calc(var(--assigned-step) * (100% / var(--max-step)))
    width: 3em
    height: 3em
    display: flex
    justify-content: center
    align-items: center
    font-weight: bold
    border-radius: 50%
    background-image: radial-gradient($border-white 55%, transparent 56%), conic-gradient($border-light-cyan 0% $step-meter, $border-x-light-gray $step-meter 100%)
</style>
